import React from "react";
import Reveal from "./Reveal";

const SERVICES = [
  {
    code: "SERVICE 01",
    name: "AI Readiness Audit",
    forWhom: "Organizations exploring AI but unsure where to start.",
    engagement: "2–4 weeks",
    bullets: [
      "Workflow audit across functions",
      "AI opportunity map",
      "Risk and governance review",
      "Prioritized 90-day roadmap",
      "ROI projections per use case",
    ],
    outcome: "A board-ready AI strategy document and a 90-day execution plan.",
    cta: "Request an audit",
    prefillInterest: "adoption_services",
  },
  {
    code: "SERVICE 02",
    name: "AI Automation Build",
    forWhom: "Organizations with identified use cases ready to deploy.",
    engagement: "8–16 weeks per build",
    bullets: [
      "AI agent development",
      "Workflow automation (n8n, Make, Zapier)",
      "Custom GPT and chatbot deployment",
      "Integration with existing systems",
      "Knowledge base and RAG systems",
      "Production deployment and handover",
    ],
    outcome: "Working AI systems in production, with internal team capability to maintain.",
    cta: "Scope a build",
    prefillInterest: "adoption_services",
  },
  {
    code: "SERVICE 03",
    name: "AI Transformation Partnership",
    forWhom: "Organizations going all-in on AI across functions.",
    engagement: "6–12 months",
    bullets: [
      "Everything in Audit + Build",
      "Workforce training across all teams",
      "AI governance and ethics framework",
      "Change management and champion programme",
      "Monthly executive briefings",
      "Quarterly board updates",
    ],
    outcome: "End-to-end AI capability internalized. Your organization becomes AI-first.",
    cta: "Explore a partnership",
    prefillInterest: "adoption_services",
  },
];

export default function AdoptionServices() {
  return (
    <section
      id="adopt"
      className="py-10 md:py-14 border-t hairline"
      data-testid="adoption-section"
    >
      <div className="container-edge">
        <Reveal>
          <p className="section-number">07 — AI ADOPTION SERVICES</p>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                className="font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[0.95] tracking-tight"
                style={{ fontWeight: 500 }}
              >
                Some companies need to learn AI.
                <br />
                <em className="italic grad-brand-text" style={{ fontWeight: 400 }}>
                  Some need it built. We do both.
                </em>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={120}>
              <p
                className="text-[16px] md:text-[18px] leading-relaxed"
                style={{ color: "var(--ink-secondary)" }}
              >
                For organizations ready to deploy AI today — not in 6 months — AI For Everyone
                offers AI Adoption Services. Audit your business. Build your automations. Train
                your teams alongside the build, so capability stays internal long after we hand
                over the keys.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.code} delay={i * 90}>
              <article className="card-edge h-full p-8 lg:p-10 flex flex-col" data-testid={`adopt-card-${i + 1}`}>
                <p className="mono-label-gold">{s.code}</p>
                <h3
                  className="mt-3 font-display text-[26px] tracking-tight leading-tight"
                  style={{ fontWeight: 500 }}
                >
                  {s.name}
                </h3>
                <p className="mt-4 italic text-[14px]" style={{ color: "var(--ink-secondary)" }}>
                  <span style={{ color: "var(--ink-tertiary)" }} className="font-mono not-italic">For: </span>
                  {s.forWhom}
                </p>
                <p className="mt-2 font-mono text-[12px]" style={{ letterSpacing: "0.12em", color: "var(--ink-tertiary)" }}>
                  ENGAGEMENT · {s.engagement.toUpperCase()}
                </p>

                <div className="mt-5">
                  <p className="mono-label mb-3">YOU GET</p>
                  <ul className="space-y-2">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="text-[13.5px] leading-relaxed pl-4 relative"
                        style={{ color: "var(--ink-secondary)" }}
                      >
                        <span
                          className="absolute left-0 top-[10px] w-2 h-px"
                          style={{ background: "var(--accent-gold)" }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5 pt-5 border-t hairline">
                  <p className="mono-label mb-2">OUTCOME</p>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--ink)" }}>
                    {s.outcome}
                  </p>
                </div>

                <a
                  href="#enquiry"
                  className="mt-6 inline-flex items-center text-[14px] link-underline text-ink font-medium"
                  data-testid={`adopt-cta-${i + 1}`}
                  onClick={() => {
                    try {
                      window.dispatchEvent(
                        new CustomEvent("enquiry:prefill", {
                          detail: { interest: s.prefillInterest, audience: "corporate" },
                        })
                      );
                    } catch (_) {}
                  }}
                >
                  {s.cta} →
                </a>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={260}>
          <p
            className="mt-12 font-display italic text-[22px] md:text-[28px] max-w-[820px]"
            style={{ color: "var(--ink-secondary)", fontWeight: 400 }}
          >
            "We don't leave you with PDFs. We leave you with working AI — and a team that can run it."
          </p>
        </Reveal>

        <Reveal delay={320}>
          <p
            className="mt-8 font-mono text-[11px] leading-relaxed"
            style={{ color: "var(--ink-tertiary)", letterSpacing: "0.12em" }}
            data-testid="adopt-trust-bar"
          >
            BUILT ON THE PRACTITIONER METHOD™ · DELIVERED BY TRAINERS WHO ARE BUILDERS ·
            POWERED BY INTIME IT SERVICES PVT. LTD. — 18+ YEARS OF ENTERPRISE SYSTEMS
          </p>
        </Reveal>
      </div>
    </section>
  );
}
